/* eslint-disable max-lines */
import { Box, Button, Typography, Stack, Grid, Paper, CircularProgress, Divider } from '@mui/material';

import React, { useEffect, useState } from 'react';

import { EntityTextField } from 'app/components/form-controls/entity-text-field';
import { StorePicker } from 'app/components/store-picker';
import { Store } from 'app/model/cris/store';
import { TaggingQualityQueue } from 'app/model/seam/tagging-quality-queue';
import { messagingService } from 'app/services/messaging.service';
import { tagQualityService } from 'app/services/tag-quality.service';
import { EntityOnChange } from 'app/type';

const MILLISECONDS_IN_SECOND = 1000;

export const TaggingQuality: React.FC = () => {
    const [currentQueue, setCurrentQueue] = useState<TaggingQualityQueue | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [primaryStore, setPrimaryStore] = React.useState<Store | undefined>();
    const [paymentStore, setPaymentStore] = React.useState<Store | undefined>();
    const [distributionStore, setDistributionStore] = React.useState<Store | undefined>();

    const fetchNextItem = (): void => {
        setLoading(true);
        tagQualityService.next().subscribe({
            next: (item: TaggingQualityQueue) => {
                setCurrentQueue(item);

                setPrimaryStore(undefined);
                setPaymentStore(item.storePayment);
                setDistributionStore(item.storeDistribution);

                setLoading(false);
            },
            error: () => {
                setLoading(false);
                messagingService.showError('Failed to fetch next item');
            },
        });
    };

    useEffect(() => {
        fetchNextItem();
    }, []);

    const handleEntityUpdate: EntityOnChange<TaggingQualityQueue, unknown> = (evt, name, value) => {
        if (!currentQueue) {
            return;
        }
        setCurrentQueue({
            ...currentQueue,
            [name]: value,
        } satisfies TaggingQualityQueue);
    };

    const handleSaveAndNext = (): void => {
        if (!currentQueue) {
            return;
        }

        if (!primaryStore) {
            messagingService.showError('Primary Store is required');

            return;
        }

        setLoading(true);
        tagQualityService
            .save({
                ...currentQueue,
                status: 2,
                storePrimary: primaryStore,
                storePayment: paymentStore,
                storeDistribution: distributionStore,
            })
            .subscribe({
                next: () => {
                    messagingService.showSuccess('Successfully updated the queue');
                    fetchNextItem();
                },
                error: () => {
                    messagingService.showError('Failed to save');
                    setLoading(false);
                },
            });
    };

    if (loading) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' minHeight='200px'>
                <CircularProgress />
            </Box>
        );
    }

    if (!currentQueue) {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' height='100vh'>
                <Typography variant='h5' color='textPrimary'>
                    Congratulations, you are done. No more items in the queue.
                </Typography>
            </Box>
        );
    }

    return (
        <Box display='flex' className='m-2' flexDirection='column'>
            <Typography variant='h4'>Tagging Quality</Typography>

            <Box sx={{ mt: 2, mb: 3 }}>
                <Paper elevation={3} className='p-2'>
                    <Grid container spacing={2}>
                        <Grid item xs={4}>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>MTID</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.mtid}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Descriptor</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.merchDesc}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Sic</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.mccSic}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={4}>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>City</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.merchCity}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>State</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.merchState}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Zip</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.merchZip}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Country</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.merchCountry}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={4}>
                            <Grid container spacing={1}>
                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>ML Tagged</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.isTaggedByAlgorithms?.toString()}
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Ground Truth</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.groundTruthSource}
                                </Grid>

                                <Grid item xs={4}>
                                    <Typography>
                                        <strong>Notes</strong>
                                    </Typography>
                                </Grid>
                                <Grid item xs={8}>
                                    {currentQueue.notes}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            <Typography variant='h6' sx={{ mb: 1.5 }}>
                Label Values
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mt: 2, mb: 5 }}>
                <Stack spacing={2}>
                    <Stack direction='row' spacing={2}>
                        <EntityTextField
                            label='Address'
                            name='labeledAddress'
                            className='mb-1 mr-1'
                            variant='outlined'
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '300px' }}
                            size='small'
                        />
                        <EntityTextField
                            label='City'
                            name='labeledCity'
                            className='mb-1 mr-1'
                            variant='outlined'
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '200px' }}
                            size='small'
                        />
                        <EntityTextField
                            label='State'
                            name='labeledState'
                            className='mb-1 mr-1'
                            variant='outlined'
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '200px' }}
                            size='small'
                        />
                        <EntityTextField
                            label='Zip'
                            name='labeledZip'
                            className='mb-1 mr-1'
                            variant='outlined'
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '200px' }}
                            size='small'
                        />
                        <EntityTextField
                            label='Country'
                            name='labeledCountry'
                            className='mb-1 mr-1'
                            variant='outlined'
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '200px' }}
                            size='small'
                        />
                    </Stack>

                    <Stack direction='row' spacing={2}>
                        <EntityTextField
                            label='Longitude'
                            name='labeledLongitude'
                            className='mb-1 mr-1'
                            type='number'
                            variant='outlined'
                            inputProps={{ step: 'any' }}
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '150px' }}
                            size='small'
                        />
                        <EntityTextField
                            label='Latitude'
                            name='labeledLatitude'
                            className='mb-1 mr-1'
                            type='number'
                            variant='outlined'
                            inputProps={{ step: 'any' }}
                            value={currentQueue}
                            onChange={handleEntityUpdate}
                            sx={{ width: '150px' }}
                            size='small'
                        />
                    </Stack>
                </Stack>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant='body1' sx={{ mb: 1.5 }}>
                Make sure to select all the relevant taxonomies on the top of the page. Store Selections below only
                search across selected taxonomies.
            </Typography>

            <Box sx={{ mt: 2, mb: 5 }}>
                <Stack direction='row' spacing={2}>
                    <StorePicker
                        label='Primary Store *'
                        size='small'
                        onChange={(event, newPrimaryStore) => setPrimaryStore(newPrimaryStore)}
                        sx={{ width: '300px' }}
                    />
                    <StorePicker
                        label='Payment Store'
                        size='small'
                        onChange={(event, newpPaymentStore) => setPaymentStore(newpPaymentStore)}
                        sx={{ width: '300px' }}
                    />
                    <StorePicker
                        label='Distribution Store'
                        size='small'
                        onChange={(event, newDistributionStore) => setDistributionStore(newDistributionStore)}
                        sx={{ width: '300px' }}
                    />
                </Stack>
            </Box>

            <Box display='flex' justifyContent='space-between' sx={{ mt: 2, mb: 3 }}>
                <Button variant='contained' color='primary' onClick={handleSaveAndNext} disabled={loading} size='large'>
                    Save and Next
                </Button>
                {currentQueue.lockExpires && (
                    <Typography variant='body2' color='textPrimary' sx={{ mt: 1 }}>
                        Record locked until:{' '}
                        {new Date(parseInt(currentQueue.lockExpires, 10) * MILLISECONDS_IN_SECOND).toLocaleString()}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
